import { useEffect } from 'react';
import { Delete } from 'lucide-react';

/**
 * Controlled PIN entry — a row of dots that fill as you type + an on-screen
 * 0–9 keypad. Hardware keyboards work too (digit / Backspace). Shared by the
 * lock screen and the setup flow. Parent owns the value and decides what
 * happens when it reaches `length` (auto-verify, no submit button).
 */
export function PinPad({
    length,
    value,
    onChange,
    shake = false,
    disabled = false,
    light = true,
}: {
    length: number;
    value: string;
    onChange: (next: string) => void;
    shake?: boolean;
    disabled?: boolean;
    /** Light dots/keys for the dark lock screen; set false on a light surface. */
    light?: boolean;
}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (disabled) return;
            if (/^[0-9]$/.test(e.key)) { if (value.length < length) onChange(value + e.key); }
            else if (e.key === 'Backspace') { onChange(value.slice(0, -1)); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [value, length, disabled, onChange]);

    const press = (d: string) => { if (!disabled && value.length < length) onChange(value + d); };
    const del = () => { if (!disabled) onChange(value.slice(0, -1)); };

    const dotBase = light ? 'border-white/40' : 'border-foreground/30';
    const dotFill = light ? 'bg-white border-white' : 'bg-primary border-primary';
    const keyCls = light
        ? 'text-white bg-white/10 hover:bg-white/20 active:bg-white/30 border border-white/10'
        : 'text-foreground bg-muted hover:bg-accent active:bg-accent/70 border';

    return (
        <div className="flex flex-col items-center gap-7 select-none">
            <div className={`flex justify-center gap-4 ${shake ? 'animate-pin-shake' : ''}`}>
                {Array.from({ length }).map((_, i) => (
                    <span
                        key={i}
                        className={`h-3.5 w-3.5 rounded-full border-2 transition-all duration-150 ${i < value.length ? `${dotFill} scale-110` : dotBase}`}
                    />
                ))}
            </div>
            <div className="grid grid-cols-3 gap-3.5">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button key={n} type="button" disabled={disabled} onClick={() => press(String(n))}
                        className={`h-16 w-16 rounded-full text-2xl font-light flex items-center justify-center transition-colors disabled:opacity-50 ${keyCls}`}>
                        {n}
                    </button>
                ))}
                <span />
                <button type="button" disabled={disabled} onClick={() => press('0')}
                    className={`h-16 w-16 rounded-full text-2xl font-light flex items-center justify-center transition-colors disabled:opacity-50 ${keyCls}`}>
                    0
                </button>
                <button type="button" disabled={disabled || value.length === 0} onClick={del} aria-label="Delete"
                    className={`h-16 w-16 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 ${light ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-accent'}`}>
                    <Delete className="h-6 w-6" />
                </button>
            </div>
        </div>
    );
}
